import React from "react";
import { Link } from "react-router-dom";

// =============================================================================
// SNIPPET CARD — Standardized Bento Dashboard Card
// =============================================================================
// PURPOSE: Wrapper for all bento grid cards. Provides consistent layout:
//   Title + count badge + compact rows + "View all →" link.
//
// USAGE:
//   <SnippetCard title="Transactions" count={142} viewAllLink="/admin/transactions">
//     {rows}
//   </SnippetCard>
// =============================================================================

interface SnippetCardProps {
  title: string;
  count?: number;
  badge?: React.ReactNode;
  viewAllLink?: string;
  viewAllLabel?: string;
  children: React.ReactNode;
  className?: string;
  /** Optional grid-area name for bento layout */
  gridArea?: string;
}

const SnippetCard: React.FC<SnippetCardProps> = ({
  title,
  count,
  badge,
  viewAllLink,
  viewAllLabel,
  children,
  className = "",
  gridArea,
}) => (
  <div
    className={`liquid-glass anim-lift p-4 flex flex-col h-full ${className}`}
    style={gridArea ? { gridArea } : undefined}
  >
    {/* Header: title + badge + view all */}
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <h3 className="token-14s">{title}</h3>
        {count !== undefined && (
          <span className="token-10 font-medium px-1.5 py-0.5 rounded bg-black/[0.05] text-glass-secondary">
            {count}
          </span>
        )}
        {badge}
      </div>
      {viewAllLink && (
        <Link
          to={viewAllLink}
          className="token-11 text-blue-400 hover:text-blue-300 transition-colors"
        >
          {viewAllLabel ?? "View all"} →
        </Link>
      )}
    </div>
    {/* Content */}
    <div className="flex-1 min-h-0">{children}</div>
  </div>
);

export default SnippetCard;
